import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import Aspect169Icon from 'assets/svgr-icons/Aspect_Ratio_16_9_Dropdown.svg'
import ArrowDownIcon from 'assets/svgr-icons/Arrow_Down.svg'
import * as Styled from './styled'
import { getAspectRatioOptions, ORIGINAL_RATIO_SENTINEL } from './consts'
import AspectRatioOption from './AspectRatioOption'
import { useAspectRatioSwitch } from 'hooks/useAspectRatioSwitch'
import { useAspectRatioWarning } from 'hooks/useAspectRatioWarning'
import useBackdropClick from 'hooks/useBackdropClick'
import AspectRatioWarningPopup from 'common-components/AspectRatioWarningPopup'
import { TCanvasSizeKey } from 'consts'
import { editorSelectors } from 'store/features/editor/selectors'
import { projectDataSelectors } from 'store/features/projectData/selectors'

const AspectRatioSelect: React.FC = () => {
  const { aspectRatioString, canSwitch, performSwitch } = useAspectRatioSwitch()
  const storedSelectedKey = useSelector(editorSelectors.selectedAspectRatioKey)
  const hasMediaItemsWithSource = useSelector(
    projectDataSelectors.hasMediaItemsWithSource
  )

  useEffect(() => {
    if (storedSelectedKey === 'original' && !hasMediaItemsWithSource) {
      performSwitch('widescreen')
    }
  }, [storedSelectedKey, hasMediaItemsWithSource, performSwitch])
  const {
    handleAspectRatioClick,
    showWarning,
    pendingSelection,
    handleConfirmSwitch,
    handleCancelSwitch
  } = useAspectRatioWarning()
  const [open, setOpen] = useState(false)
  const wrapperRef = useBackdropClick<HTMLDivElement>(() => {
    if (open) {
      setOpen(false)
    }
  })

  const options = getAspectRatioOptions().map(opt => ({
    ...opt,
    icon: <opt.icon />
  }))

  // Prefer the key the user actually picked (session-scoped). Falls back to
  // matching by aspect-ratio string for projects loaded fresh from the API,
  // where no in-session selection has happened yet.
  const selectedKey: TCanvasSizeKey | undefined =
    storedSelectedKey ??
    options.find(o => o.ratio === aspectRatioString)?.key ??
    'original'

  const isOptionDisabled = (key: TCanvasSizeKey) =>
    key === 'original' && !hasMediaItemsWithSource

  const onSelect = (key: TCanvasSizeKey) => {
    if (!canSwitch) {
      setOpen(false)
      return
    }
    if (isOptionDisabled(key)) {
      return
    }
    handleAspectRatioClick(key)
    setOpen(false)
  }

  const currentOption = options.find(o => o.key === selectedKey)
  const currentIcon = currentOption?.icon ?? <Aspect169Icon />
  const currentLabel =
    selectedKey === 'original' && currentOption
      ? currentOption.label
      : aspectRatioString

  return (
    <>
      <Styled.Wrapper ref={wrapperRef}>
        <Styled.Trigger
          $isOpen={open}
          $disabled={!canSwitch}
          onClick={() => canSwitch && setOpen(v => !v)}
        >
          <Styled.Left>
            <Styled.IconBox>{currentIcon}</Styled.IconBox>
            <Styled.Num>{currentLabel}</Styled.Num>
          </Styled.Left>
          <Styled.ArrowWrapper $isOpen={open}>
            <ArrowDownIcon />
          </Styled.ArrowWrapper>
        </Styled.Trigger>
        {open && (
          <Styled.Panel>
            {options.map(opt => (
              <AspectRatioOption
                key={opt.key}
                label={opt.label}
                ratio={opt.ratio === ORIGINAL_RATIO_SENTINEL ? '' : opt.ratio}
                icon={opt.icon}
                isSelected={opt.key === selectedKey}
                disabled={isOptionDisabled(opt.key)}
                onClick={() => onSelect(opt.key)}
              />
            ))}
          </Styled.Panel>
        )}
      </Styled.Wrapper>

      <AspectRatioWarningPopup
        isOpen={showWarning}
        pendingSelection={pendingSelection}
        onConfirm={handleConfirmSwitch}
        onCancel={handleCancelSwitch}
      />
    </>
  )
}

export default AspectRatioSelect
